// §4 S2's answer to a refused close — `WO_CLOSE_BLOCKED` in the reader's
// language, with the way out (D-299 owner ruling R10, spec 04 §3.1 / §4 S2).
//
// WHY A COMPONENT AND NOT THE SERVER'S SENTENCE. The close is `production.wo.
// close`, which finance holds as well as the admins, and the refusal's old
// `detail` read "End it first from §4 S13 Blocked stations (§5 e35, POST
// /v1/downtime/{id}/end)…" — spec numbers and an HTTP route, in English under a
// Russian headline, pointing finance at a board they cannot open and a button
// they do not have (round-2 refutation). The refusal now carries its blockers
// as data; this note words each one and says what the READER can do about it:
//
//  * a reader who can clear a blocker gets a LINK to the one screen that
//    clears it — S13 for an open stoppage (e35 needs `production.conflict.
//    resolve`, and the board itself `production.telemetry.read`), S7 for an
//    open conflict (e25, `production.conflict.resolve`), S5 for an undecided
//    hand-off (e18/e19, `production.approve_shift_handoff`, on a board read
//    with `production.scoreboard.read`);
//  * a reader who cannot gets a SENTENCE NAMING WHO CAN, never a link to a
//    screen that would refuse them.
//
// All three blockers are named at once when the refusal carries them, so the
// reader is not sent round a close-refuse-close loop one arm at a time.

import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useProductionPermissions } from "../hooks/permissions";
import { formatTimestamp, type CloseBlockers } from "../lib/format";

export function CloseBlockedNote({
  blockers,
  facilityId,
}: {
  readonly blockers: CloseBlockers;
  /** The work order's facility — S13 opens on it, so the stoppage is on the
   * first screen the reader sees. */
  readonly facilityId: string;
}) {
  const { t, i18n } = useTranslation();
  const perms = useProductionPermissions();
  const canEndStoppage =
    perms.has("production.conflict.resolve") && perms.has("production.telemetry.read");
  const canResolveConflict = perms.has("production.conflict.resolve");
  const canDecideHandoff =
    perms.has("production.approve_shift_handoff") && perms.has("production.scoreboard.read");
  // The starts the refusal actually carries (D-299 R10). `closeBlockersOf`
  // keeps a listed stoppage with no string `started_at` — it is still an open
  // stoppage holding the close, so it counts — and only its instant is
  // unknown. Filtered BEFORE the length test, so a list whose entries all
  // lack a start leaves the since-sentence out instead of printing it empty.
  const startedTimes = blockers.stoppages.flatMap((s) =>
    s.startedAt !== undefined ? [formatTimestamp(s.startedAt, i18n.language)] : [],
  );

  return (
    <ul className="wo-close-blocked" data-testid="wo-close-blocked">
      {blockers.openDowntime > 0 ? (
        <li data-testid="wo-close-blocked-downtime">
          {t("production.detail.closeBlocked.downtime", { count: blockers.openDowntime })}
          {startedTimes.length > 0 ? (
            <span className="production-hint" data-testid="wo-close-blocked-downtime-since">
              {" "}
              {t("production.detail.closeBlocked.downtimeSince", {
                times: new Intl.ListFormat(i18n.language, { type: "conjunction" }).format(
                  startedTimes,
                ),
              })}
            </span>
          ) : null}{" "}
          {canEndStoppage ? (
            <Link
              to="/production/blocked"
              search={{ facility_id: facilityId }}
              className="ds-lnk"
              data-testid="wo-close-blocked-downtime-link"
            >
              {t("production.detail.closeBlocked.downtimeLink")}
            </Link>
          ) : (
            <span data-testid="wo-close-blocked-downtime-who">
              {t("production.detail.closeBlocked.downtimeWho")}
            </span>
          )}
        </li>
      ) : null}
      {blockers.openConflicts > 0 ? (
        <li data-testid="wo-close-blocked-conflicts">
          {t("production.detail.closeBlocked.conflicts", { count: blockers.openConflicts })}{" "}
          {canResolveConflict ? (
            <Link
              to="/production/conflicts"
              className="ds-lnk"
              data-testid="wo-close-blocked-conflicts-link"
            >
              {t("production.detail.closeBlocked.conflictsLink")}
            </Link>
          ) : (
            <span data-testid="wo-close-blocked-conflicts-who">
              {t("production.detail.closeBlocked.conflictsWho")}
            </span>
          )}
        </li>
      ) : null}
      {blockers.pendingHandoffs > 0 ? (
        <li data-testid="wo-close-blocked-handoffs">
          {t("production.detail.closeBlocked.handoffs", { count: blockers.pendingHandoffs })}{" "}
          {canDecideHandoff ? (
            <Link
              to="/production/shifts"
              className="ds-lnk"
              data-testid="wo-close-blocked-handoffs-link"
            >
              {t("production.detail.closeBlocked.handoffsLink")}
            </Link>
          ) : (
            <span data-testid="wo-close-blocked-handoffs-who">
              {t("production.detail.closeBlocked.handoffsWho")}
            </span>
          )}
        </li>
      ) : null}
    </ul>
  );
}
